import type {
  MarkContext,
  MarkContextSummary,
  MarkContextSummaryRow,
  MarkContextTarget,
} from 'containers/admin/clients/client/tabs-components/ai-assistant/mark-panel/mark-panel.types';

import { getPrimaryMetric } from 'containers/user/critical-elements-monitoring/helpers/get-primary-metric';
import {
  formatMetricValue,
  formatSignedMonitoringValue,
} from 'containers/user/critical-elements-monitoring/helpers/format-monitoring-value';
import {
  ALL_EXTEND_WITH_OPTIONS,
  getExtensionDisplayLabel,
  getStageDisplayLabel,
  NO_VALUE_GLYPH,
} from 'containers/user/critical-elements-monitoring/monitoring-constants';
import type { StageDisplayMode } from 'containers/user/critical-elements-monitoring/monitoring-constants';
import type {
  CompareTo,
  CriticalElementMonitoringMap,
  CriticalElementSummary,
  ExtendWithOption,
  FunnelSection,
  FunnelStageKey,
  FunnelStageMetric,
  MetricValue,
  PercentMetricValue,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

import { getMonitoringPrompts } from 'containers/user/critical-elements-monitoring/mark/monitoring-mark-prompts';
import type { MonitoringPromptScope } from 'containers/user/critical-elements-monitoring/mark/monitoring-mark-prompt-catalog';

export const MONITORING_MARK_SECTION = {
  totalFunnel: 'totalFunnel',
  criticalElements: 'criticalElements',
  criticalElement: 'criticalElement',
} as const satisfies Record<string, string>;

export type MonitoringMarkSection =
  (typeof MONITORING_MARK_SECTION)[keyof typeof MONITORING_MARK_SECTION];

export interface MonitoringMarkSnapshot {
  enteringClass: number;
  compareTo: CompareTo;
  cohortLabel: string;
  displayMode: StageDisplayMode;
  ceDisplayMode: StageDisplayMode;
  extend: ExtendWithOption[];
  metrics: CriticalElementMonitoringMap | undefined;
  criticalElements: CriticalElementSummary[];
}

const MONITORING_PAGE_LABEL = 'Critical Elements';

const TOTAL_FUNNEL_LABEL = 'Total funnel';

const NET_DEPOSIT_YIELD_UNIT = '%';

const FUNNEL_STAGE_KEYS: readonly FunnelStageKey[] = [
  'inquiry',
  'application',
  'admit',
  'netDeposit',
];

const isFunnelStageKey = (value: string): value is FunnelStageKey =>
  (FUNNEL_STAGE_KEYS as readonly string[]).includes(value);

const isExtendWithOption = (value: string): value is ExtendWithOption =>
  (ALL_EXTEND_WITH_OPTIONS as readonly string[]).includes(value);

const isStageDisplayMode = (
  value: string | undefined
): value is StageDisplayMode =>
  value === 'volume' || value === 'rate' || value === 'proportion';

const resolveDisplayMode = (value: string | undefined): StageDisplayMode =>
  isStageDisplayMode(value) ? value : 'volume';

const resolveStage = (
  funnel: FunnelSection,
  stageKey: FunnelStageKey
): FunnelStageMetric | undefined =>
  funnel.stages.find((stage) => stage.stageKey === stageKey);

interface PrimaryMetricSource {
  volume: MetricValue;
  rate: PercentMetricValue;
  proportion: PercentMetricValue;
}

const getPrimaryValue = (
  metric: PrimaryMetricSource,
  displayMode: StageDisplayMode
): number | null => {
  if (displayMode === 'rate') return metric.rate.current;
  if (displayMode === 'proportion') return metric.proportion.current;

  return metric.volume.current;
};

export const isMarkTriggerEligible = (
  metric: Pick<
    FunnelStageMetric,
    'dataStatus' | 'volume' | 'rate' | 'proportion'
  >,
  displayMode: StageDisplayMode
): boolean =>
  !metric.dataStatus && getPrimaryMetric(metric, displayMode).current !== null;

export const hasMonitoringMarkData = (
  metrics: CriticalElementMonitoringMap | undefined
): boolean =>
  !!metrics &&
  [...metrics.total.stages, ...metrics.total.extensions].some((metric) =>
    isMarkTriggerEligible(metric, 'volume')
  );

const formatVolume = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? NO_VALUE_GLYPH
    : value.toLocaleString();

const formatNetDepositYield = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? NO_VALUE_GLYPH
    : formatSignedMonitoringValue(value, NET_DEPOSIT_YIELD_UNIT);

const buildFunnelSummary = (
  title: string,
  scopeLabel: string,
  enteringClass: number,
  funnel: FunnelSection
): MarkContextSummary => {
  const inquiry = resolveStage(funnel, 'inquiry');
  const application = resolveStage(funnel, 'application');
  const netDeposit = resolveStage(funnel, 'netDeposit');

  const rows: MarkContextSummaryRow[] = [
    { label: 'Scope', value: scopeLabel },
    { label: 'Entering class', value: String(enteringClass) },
    { label: 'Inquiries', value: formatVolume(inquiry?.volume.current) },
    { label: 'Applications', value: formatVolume(application?.volume.current) },
    { label: 'Net deposits', value: formatVolume(netDeposit?.volume.current) },
    {
      label: 'Net deposit yield (YoY)',
      value: formatNetDepositYield(netDeposit?.rate.pp),
    },
  ];

  return { title, rows };
};

const getCriticalElementName = (
  snapshot: MonitoringMarkSnapshot,
  entityId: string | undefined
): string | undefined =>
  entityId
    ? snapshot.criticalElements.find((ce) => ce.id === entityId)?.name
    : undefined;

const buildContextKey = (parts: Array<string | undefined>): string =>
  parts.filter((part): part is string => Boolean(part)).join(':');

const buildPageContext = (snapshot: MonitoringMarkSnapshot): MarkContext => ({
  kind: 'page',
  key: buildContextKey(['monitoring', 'page', snapshot.compareTo]),
  surface: 'monitoring',
  section: MONITORING_MARK_SECTION.criticalElements,
  label: MONITORING_PAGE_LABEL,
  prompts: getMonitoringPrompts({
    scope: 'criticalElements',
    compareTo: snapshot.compareTo,
    displayMode: 'volume',
    card: null,
  }),
});

const buildTotalFunnelSectionContext = (
  target: Extract<MarkContextTarget, { kind: 'section' }>,
  snapshot: MonitoringMarkSnapshot
): MarkContext | null => {
  const funnel = snapshot.metrics?.total;

  if (!funnel) return null;

  return {
    kind: 'section',
    sectionId: target.sectionId,
    key: buildContextKey([
      'monitoring',
      'section',
      target.sectionId,
      snapshot.displayMode,
      snapshot.compareTo,
    ]),
    surface: 'monitoring',
    section: MONITORING_MARK_SECTION.totalFunnel,
    label: TOTAL_FUNNEL_LABEL,
    summary: buildFunnelSummary(
      TOTAL_FUNNEL_LABEL,
      TOTAL_FUNNEL_LABEL,
      snapshot.enteringClass,
      funnel
    ),
    prompts: getMonitoringPrompts({
      scope: 'topOfFunnel',
      compareTo: snapshot.compareTo,
      displayMode: snapshot.displayMode,
      card: null,
    }),
  };
};

const buildTileContext = (
  target: Extract<MarkContextTarget, { kind: 'tile' }>,
  snapshot: MonitoringMarkSnapshot
): MarkContext | null => {
  const isCriticalElementScope =
    target.sectionId === MONITORING_MARK_SECTION.criticalElement;

  const funnel = isCriticalElementScope
    ? target.entityId
      ? snapshot.metrics?.byCriticalElementId[target.entityId]
      : undefined
    : snapshot.metrics?.total;

  if (!funnel) return null;

  const scopeLabel = isCriticalElementScope
    ? getCriticalElementName(snapshot, target.entityId)
    : TOTAL_FUNNEL_LABEL;

  if (!scopeLabel) return null;

  const displayMode = resolveDisplayMode(target.displayMode);
  const promptScope: MonitoringPromptScope = isCriticalElementScope
    ? 'criticalElement'
    : 'totalFunnel';

  if (isFunnelStageKey(target.tileId)) {
    const stage = resolveStage(funnel, target.tileId);

    if (!stage || stage.dataStatus) return null;

    const primaryValue = getPrimaryValue(stage, displayMode);

    if (primaryValue === null) return null;

    const stageLabel = getStageDisplayLabel(target.tileId, displayMode);
    const label = `${scopeLabel}, ${stageLabel}, ${formatMetricValue(
      primaryValue,
      displayMode
    )}`;

    return {
      kind: 'tile',
      sectionId: target.sectionId,
      tileId: target.tileId,
      entityId: target.entityId,
      displayMode: target.displayMode,
      key: buildContextKey([
        'monitoring',
        'tile',
        target.sectionId,
        target.entityId ?? 'total',
        target.tileId,
        displayMode,
        snapshot.compareTo,
      ]),
      surface: 'monitoring',
      section: target.sectionId,
      label,
      summary: buildFunnelSummary(
        label,
        scopeLabel,
        snapshot.enteringClass,
        funnel
      ),
      prompts: getMonitoringPrompts({
        scope: promptScope,
        compareTo: snapshot.compareTo,
        displayMode,
        card: target.tileId,
      }),
    };
  }

  if (isExtendWithOption(target.tileId)) {
    const extension = funnel.extensions.find(
      (item) => item.type === target.tileId
    );

    if (!extension || extension.dataStatus) return null;

    const primaryValue = getPrimaryValue(extension, displayMode);

    if (primaryValue === null) return null;

    const extensionLabel = getExtensionDisplayLabel(target.tileId, displayMode);
    const label = `${scopeLabel}, ${extensionLabel}, ${formatMetricValue(
      primaryValue,
      displayMode
    )}`;

    return {
      kind: 'tile',
      sectionId: target.sectionId,
      tileId: target.tileId,
      entityId: target.entityId,
      displayMode: target.displayMode,
      key: buildContextKey([
        'monitoring',
        'tile',
        target.sectionId,
        target.entityId ?? 'total',
        target.tileId,
        displayMode,
        snapshot.compareTo,
      ]),
      surface: 'monitoring',
      section: target.sectionId,
      label,
      summary: buildFunnelSummary(
        label,
        scopeLabel,
        snapshot.enteringClass,
        funnel
      ),
      prompts: getMonitoringPrompts({
        scope: promptScope,
        compareTo: snapshot.compareTo,
        displayMode,
        card: target.tileId,
      }),
    };
  }

  return null;
};

const buildEntityContext = (
  target: Extract<MarkContextTarget, { kind: 'entity' }>,
  snapshot: MonitoringMarkSnapshot
): MarkContext | null => {
  if (target.entityType !== 'criticalElement') return null;

  const funnel = snapshot.metrics?.byCriticalElementId[target.entityId];
  const name = getCriticalElementName(snapshot, target.entityId);

  if (!funnel || !name) return null;

  return {
    kind: 'entity',
    entityType: target.entityType,
    entityId: target.entityId,
    key: buildContextKey([
      'monitoring',
      'entity',
      target.entityType,
      target.entityId,
      snapshot.compareTo,
    ]),
    surface: 'monitoring',
    section: MONITORING_MARK_SECTION.criticalElement,
    label: name,
    summary: buildFunnelSummary(name, name, snapshot.enteringClass, funnel),
    prompts: getMonitoringPrompts({
      scope: 'criticalElements',
      compareTo: snapshot.compareTo,
      displayMode: 'volume',
      card: null,
    }),
  };
};

export const resolveMonitoringMarkContext = (
  target: MarkContextTarget,
  snapshot: MonitoringMarkSnapshot
): MarkContext | null => {
  switch (target.kind) {
    case 'page':
      return buildPageContext(snapshot);

    case 'section':
      return target.sectionId === MONITORING_MARK_SECTION.totalFunnel
        ? buildTotalFunnelSectionContext(target, snapshot)
        : null;

    case 'tile':
      return buildTileContext(target, snapshot);

    case 'entity':
      return buildEntityContext(target, snapshot);

    default:
      return null;
  }
};
